/* eslint-disable */ // @ts-nocheck
import React, { Fragment, useRef, useState } from 'react';
import fetch from 'isomorphic-fetch';
import { v4 as uuidv4 } from 'uuid';
import Alert from 'react-bootstrap/Alert';
import './bs-tooltip.min.css';

import { Stages, Form } from 'react-stages';
import fields from './fields';

import i18n from './ech-i18n';
import Navigation from './Navigation';
import Actions from './Actions';
import Upload from './UploadInput';
import StyledContainer from './StyledContainer';

fields.upload = {
  component: Upload,
  isValid: (value, config) => {
    if (config.isRequired && (value === '' || typeof value === 'undefined'))
      return false;
    return true;
  },
};

fields.email.isValid = (value, config) => {
  if (
    config.isRequired &&
    (value === '' ||
      typeof value === 'undefined' ||
      value.indexOf('@') === -1 ||
      value.indexOf('.') === -1)
  )
    return false;
  return true;
};

import {
  peopleConfig,
  projectConfig,
  costsConfig,
  revenuesConfig,
  commentsConfig,
  PeopleRenderer,
  ProjectRenderer,
  CostsRenderer,
  RevenuesRenderer,
  CommentsRenderer,
} from './steps';

const FormLayout = ({ fields, actions }) => (
  <div>
    {fields}
    <hr />
    {actions}
  </div>
);

let fullWizardData = {};

const cleanUpData = (o) => {
  for (const k in o) {
    if (!o[k] || typeof o[k] !== 'object') {
      continue; // If null or not an object, skip to the next iteration
    }

    // The property is an object
    cleanUpData(o[k]); // <-- Make a recursive call on the nested object
    if (Object.keys(o[k]).length === 0 && !Array.isArray(o[k])) {
      delete o[k]; // The object had no properties, so delete that property
    } else if (Object.keys(o[k]).length === 0 && Array.isArray(o[k])) {
      o[k] = [];
    }
  }
  return o;
};

const SubsidiesWizard = ({ locale, saveAPI, uploadAPI, forceStep }) => {
  const [sentSuccessfully, setSentSuccessfully] = useState(false);
  const [formIsDisabled, setFormIsDisabled] = useState(false);
  const [sentWithError, setSentWithError] = useState(false);
  const [formId, setFormId] = useState(uuidv4());
  const containerRef = useRef(null);

  const onSubmit = async (data, locale) => {
    const cleanData = JSON.parse(JSON.stringify(data));
    cleanUpData(cleanData);

    if (saveAPI && !formIsDisabled) {
      setFormIsDisabled(true);

      await fetch(saveAPI, {
        method: 'post',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(Object.assign(cleanData, { language: locale })),
      })
        .then(function (response) {
          setSentWithError(false);
          return response.json();
        })
        .then(function (json) {
          console.log(json);
          setSentSuccessfully(true);
          setFormIsDisabled(false);
        })
        .catch(function (err) {
          console.log(err);
          setSentWithError(true);
          setFormIsDisabled(false);
        });
    }
  };

  const handleChange = (data) => {
    fullWizardData = data;
  };

  const createActionButtonConfig = (
    type,
    onNav,
    onSubmit,
    data,
    locale,
    reset,
  ) => {
    if (type === 'first') {
      return [
        {
          title: i18n.actions.cancel[locale],
          validate: false,
          onClick: () => {
            reset();
            location.reload();
          },
        },
        {
          title: i18n.actions.nextStep[locale],
          type: 'primary',
          validate: true,
          onClick: () => {
            containerRef.current.scrollIntoView();
            onNav('next');
          },
        },
      ];
    }
    return [
      {
        title: i18n.actions.cancel[locale],
        validate: false,
        onClick: () => {
          reset();
          location.reload();
        },
      },
      {
        title: i18n.actions.prevStep[locale],
        type: 'secondary',
        validate: false,
        onClick: () => {
          containerRef.current.scrollIntoView();
          onNav('prev');
        },
      },
      {
        title:
          type === 'last'
            ? i18n.actions.send[locale]
            : i18n.actions.nextStep[locale],
        type: 'primary',
        validate: true,
        onClick: () => {
          if (type === 'last') {
            onSubmit(data, locale);
          } else {
            containerRef.current.scrollIntoView();
            onNav('next');
          }
        },
      },
    ];
  };

  const Heading = ({ primaryText, secondaryText }) => {
    return (
      <h2
        style={{
          fontSize: '12px',
          fontWeight: 'regular',
          marginTop: '4px',
          marginBottom: '32px',
        }}
      >
        <strong>{primaryText}</strong> - {secondaryText}
      </h2>
    );
  };

  if (sentSuccessfully) {
    return (
      <>
        <StyledContainer
          ref={containerRef}
          style={{ paddingTop: '94px', marginBottom: '32px' }}
        >
          <p>{i18n.sentNote[locale]}</p>
        </StyledContainer>
      </>
    );
  }

  return (
    <>
      <StyledContainer
        ref={containerRef}
        style={{
          paddingTop: '94px',
          marginBottom: '32px',
          pointerEvents: formIsDisabled ? 'none' : 'auto',
        }}
      >
        <Stages
          id={`subsidiesForm${locale}`}
          onChange={({ data, errors }) => handleChange(data, errors)}
          autoSave={{
            type: 'local',
            validDataOnly: false,
          }}
          initialStep={
            typeof forceStep === 'number' || typeof forceStep === 'string'
              ? Number(forceStep)
              : undefined
          }
          validateOnStepChange={
            typeof forceStep === 'number' || typeof forceStep === 'string'
              ? false
              : true
          }
          render={({ navigationProps, steps }) => (
            <div>
              <Navigation {...navigationProps} />
              {steps}
            </div>
          )}
        >
          {({
            data,
            allData,
            isActive,
            onChange,
            onNav,
            index,
            setStepKey,
            initializing,
            reset,
          }) => {
            const key = setStepKey('involvedActors', index);
            if (initializing) return null;

            return (
              <Fragment key={`step-${key}`}>
                {isActive ? (
                  <Heading
                    primaryText={i18n.steps[1].title[locale]}
                    secondaryText={i18n.steps[1].subTitle[locale]}
                  />
                ) : null}
                <Form
                  id={index}
                  data={data}
                  config={peopleConfig(locale, fullWizardData)}
                  render={({ actionProps, fieldProps, loading }) => (
                    <FormLayout
                      loading={loading}
                      fields={
                        <PeopleRenderer {...fieldProps} locale={locale} />
                      }
                      actions={
                        <Actions
                          config={createActionButtonConfig(
                            'first',
                            onNav,
                            onSubmit,
                            allData,
                            locale,
                            reset,
                          )}
                          {...actionProps}
                          isDisabled={formIsDisabled || actionProps.isDisabled}
                        />
                      }
                    />
                  )}
                  fields={fields}
                  onChange={onChange}
                  isVisible={isActive}
                />
              </Fragment>
            );
          }}

          {({
            data,
            allData,
            isActive,
            onChange,
            onNav,
            index,
            setStepKey,
            initializing,
            reset,
          }) => {
            const key = setStepKey('generalInformation', index);
            if (initializing) return null;

            return (
              <Fragment key={`step-${key}`}>
                {isActive ? (
                  <Heading
                    primaryText={i18n.steps[2].title[locale]}
                    secondaryText={i18n.steps[2].subTitle[locale]}
                  />
                ) : null}
                <Form
                  id={index}
                  data={data}
                  config={projectConfig(locale, fullWizardData)}
                  render={({ actionProps, fieldProps, loading }) => (
                    <FormLayout
                      loading={loading}
                      fields={
                        <ProjectRenderer {...fieldProps} locale={locale} />
                      }
                      actions={
                        <Actions
                          config={createActionButtonConfig(
                            'regular',
                            onNav,
                            onSubmit,
                            allData,
                            locale,
                            reset,
                          )}
                          {...actionProps}
                          isDisabled={formIsDisabled || actionProps.isDisabled}
                        />
                      }
                    />
                  )}
                  fields={fields}
                  onChange={onChange}
                  isVisible={isActive}
                />
              </Fragment>
            );
          }}

          {({
            data,
            allData,
            isActive,
            onChange,
            onNav,
            index,
            setStepKey,
            initializing,
            reset,
          }) => {
            const key = setStepKey('projectCosts', index);
            if (initializing) return null;

            return (
              <Fragment key={`step-${key}`}>
                {isActive ? (
                  <Heading
                    primaryText={i18n.steps[3].title[locale]}
                    secondaryText={i18n.steps[3].subTitle[locale]}
                  />
                ) : null}
                <Form
                  id={index}
                  data={data}
                  config={costsConfig(locale, fullWizardData)}
                  render={({ actionProps, fieldProps, loading }) => (
                    <FormLayout
                      loading={loading}
                      fields={<CostsRenderer {...fieldProps} locale={locale} />}
                      actions={
                        <Actions
                          config={createActionButtonConfig(
                            'regular',
                            onNav,
                            onSubmit,
                            allData,
                            locale,
                            reset,
                          )}
                          {...actionProps}
                          isDisabled={formIsDisabled || actionProps.isDisabled}
                        />
                      }
                    />
                  )}
                  fields={fields}
                  onChange={onChange}
                  isVisible={isActive}
                />
              </Fragment>
            );
          }}

          {({
            data,
            allData,
            isActive,
            onChange,
            onNav,
            index,
            setStepKey,
            initializing,
            reset,
          }) => {
            const key = setStepKey('projectFinances', index);
            if (initializing) return null;

            return (
              <Fragment key={`step-${key}`}>
                {isActive ? (
                  <Heading
                    primaryText={i18n.steps[4].title[locale]}
                    secondaryText={i18n.steps[4].subTitle[locale]}
                  />
                ) : null}
                <Form
                  id={index}
                  data={data}
                  config={revenuesConfig(locale, fullWizardData)}
                  render={({ actionProps, fieldProps, loading }) => (
                    <FormLayout
                      loading={loading}
                      fields={
                        <RevenuesRenderer {...fieldProps} locale={locale} />
                      }
                      actions={
                        <Actions
                          config={createActionButtonConfig(
                            'regular',
                            onNav,
                            onSubmit,
                            allData,
                            locale,
                            reset,
                          )}
                          {...actionProps}
                          isDisabled={formIsDisabled || actionProps.isDisabled}
                        />
                      }
                    />
                  )}
                  fields={fields}
                  onChange={onChange}
                  isVisible={isActive}
                />
              </Fragment>
            );
          }}

          {({
            data,
            allData,
            isActive,
            onChange,
            onNav,
            index,
            setStepKey,
            initializing,
            reset,
          }) => {
            const key = setStepKey('additionalInfo', index);
            if (initializing) return null;

            return (
              <Fragment key={`step-${key}`}>
                {isActive ? (
                  <Heading
                    primaryText={i18n.steps[5].title[locale]}
                    secondaryText={i18n.steps[5].subTitle[locale]}
                  />
                ) : null}
                <Form
                  id={index}
                  data={data}
                  config={commentsConfig(
                    locale,
                    fullWizardData,
                    uploadAPI,
                    formId,
                  )}
                  render={({ actionProps, fieldProps, loading }) => (
                    <FormLayout
                      loading={loading}
                      fields={
                        <CommentsRenderer {...fieldProps} locale={locale} />
                      }
                      actions={
                        <Actions
                          config={createActionButtonConfig(
                            'last',
                            onNav,
                            onSubmit,
                            allData,
                            locale,
                            reset,
                          )}
                          {...actionProps}
                          isDisabled={formIsDisabled || actionProps.isDisabled}
                        />
                      }
                    />
                  )}
                  fields={fields}
                  onChange={onChange}
                  isVisible={isActive}
                />
              </Fragment>
            );
          }}
        </Stages>
        {sentWithError ? (
          <Alert
            variant="danger"
            onClose={() => setSentWithError(false)}
            dismissible
          >
            <p>{i18n.sentErrorNote[locale]}</p>
          </Alert>
        ) : null}
      </StyledContainer>
    </>
  );
};

export default SubsidiesWizard;
